<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { ToggleGroupRoot, ToggleGroupItem } from 'reka-ui';

const props = defineProps({
  conversation: { type: Object, required: true },
  hasAttachments: { type: Boolean, default: false },
});

const privateReply = defineModel({ type: Boolean, default: false });

const post = computed(() => props.conversation?.additional_attributes || {});

const { t } = useI18n();

const MODE = { PUBLIC: 'public', PRIVATE: 'private' };

const options = computed(() => [
  {
    value: MODE.PUBLIC,
    icon: 'i-lucide-message-square',
    label: t('CONVERSATION.FACEBOOK_COMMENT.MODE.PUBLIC'),
  },
  {
    value: MODE.PRIVATE,
    icon: 'i-lucide-send',
    label: t('CONVERSATION.FACEBOOK_COMMENT.MODE.PRIVATE'),
  },
]);

const mode = computed(() => (privateReply.value ? MODE.PRIVATE : MODE.PUBLIC));

// ToggleGroup single mode emits '' when the active item is clicked again;
// a reply mode must always be selected, so ignore that.
const onModeChange = value => {
  if (!value) return;
  privateReply.value = value === MODE.PRIVATE;
};

const hint = computed(() => {
  if (!privateReply.value)
    return t('CONVERSATION.FACEBOOK_COMMENT.HINT.PUBLIC');
  return props.hasAttachments
    ? t('CONVERSATION.FACEBOOK_COMMENT.HINT.PRIVATE_NO_FILES')
    : t('CONVERSATION.FACEBOOK_COMMENT.HINT.PRIVATE');
});
</script>

<template>
  <div class="mt-2 flex flex-col gap-1.5 text-xs">
    <div
      class="flex items-center gap-1.5 rounded-md bg-n-brand/[0.07] ring-1 ring-inset ring-n-brand/10 py-1 ps-2 pe-2 text-n-slate-11"
    >
      <span class="i-lucide-facebook size-3.5 flex-shrink-0 text-n-brand" />
      <span class="truncate flex-grow">
        {{ $t('CONVERSATION.FACEBOOK_COMMENT.ON_POST') }}
        <span class="text-n-slate-12">
          {{
            post.post_message ||
            $t('CONVERSATION.FACEBOOK_COMMENT.UNTITLED_POST')
          }}
        </span>
      </span>
      <a
        v-if="post.post_permalink_url"
        :href="post.post_permalink_url"
        target="_blank"
        rel="noopener noreferrer"
        class="flex-shrink-0 font-medium text-n-brand hover:underline"
      >
        {{ $t('CONVERSATION.FACEBOOK_COMMENT.VIEW_POST') }}
      </a>
    </div>
    <div class="flex flex-wrap items-center gap-2">
      <ToggleGroupRoot
        type="single"
        :model-value="mode"
        class="inline-flex rounded-lg bg-n-alpha-2 p-0.5"
        @update:model-value="onModeChange"
      >
        <ToggleGroupItem
          v-for="option in options"
          :key="option.value"
          :value="option.value"
          class="flex items-center gap-1 rounded-md px-2 py-1 text-n-slate-11 outline-none transition-colors hover:text-n-slate-12 data-[state=on]:bg-n-brand data-[state=on]:text-white data-[state=on]:shadow-sm"
        >
          <span :class="option.icon" class="size-3.5" />
          {{ option.label }}
        </ToggleGroupItem>
      </ToggleGroupRoot>
      <span
        class="text-n-slate-11"
        :class="{ 'text-n-ruby-11': privateReply && hasAttachments }"
      >
        {{ hint }}
      </span>
    </div>
  </div>
</template>
